import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import DailyCheckConfirmModal from "../../../componant/modal/DailyCheckConfirmModal";
import { postDcSelDCListByMonth } from "../../../api/dailyCheck";
import { toast } from "wc-toast";
import Loading from "../../../componant/loading/Loading";

export default function DailyCheckCalendar() {

    const isIOS = () => {   return /(iPad|iPhone|iPod|Mac).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent) && !window.MSStream; };
    
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [dataDownloading, setDataDownloading] = useState(false);

    const monthInputRef = useRef(null);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [targetDate, setTargetDate] = useState("");

    const [currentDate, setCurrentDate] = useState(dayjs());
    const year = currentDate.year();
    const month = currentDate.month();
    const today = dayjs();
    const weekdaysShort = ["일", "월", "화", "수", "목", "금", "토"];


    const [dbHistoryList, setDbHistoryList] = useState([]);

    const callPostDcSelDCListByMonth = async (targetYearMonth) => {
        try {

            const response = await postDcSelDCListByMonth({ check_month: targetYearMonth });
            if (response?.code === 1000) {
                setDbHistoryList(response?.resultList || []);
            } else {
                console.log("월별 점검 현황을 불러오는데 실패하였습니다.");
            }
        } catch (err) {
            console.error("월별 점검 리스트 로드 중 에러 발생:", err);
        } finally {
            setIsLoading(false);
        }
    };


    useEffect(() => {
        const currentYearMonth = currentDate.format("YYYY-MM");
        callPostDcSelDCListByMonth(currentYearMonth);
    }, [currentDate]);

    const handlePrevMonth = () => setCurrentDate(currentDate.subtract(1, "month"));
    const handleNextMonth = () => setCurrentDate(currentDate.add(1, "month"));
    const handleTitleClick = () => monthInputRef.current?.showPicker();
    const handleMonthInputChange = (e) => e.target.value && setCurrentDate(dayjs(e.target.value));


    const startOfMonth = currentDate.startOf("month");
    const startDay = startOfMonth.day();
    const daysInMonth = currentDate.daysInMonth();
    const totalCells = 42;
    const daysArray = [];

    const prevMonthDate = currentDate.subtract(1, "month");
    const daysInPrevMonth = prevMonthDate.daysInMonth();
    for (let i = startDay - 1; i >= 0; i--) {
        daysArray.push({ day: daysInPrevMonth - i, currentMonth: false, date: prevMonthDate.date(daysInPrevMonth - i) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
        daysArray.push({ day: i, currentMonth: true, date: currentDate.date(i) });
    }
    const nextCount = totalCells - daysArray.length;
    const nextMonthDate = currentDate.add(1, "month");
    for (let i = 1; i <= nextCount; i++) {
        daysArray.push({ day: i, currentMonth: false, date: nextMonthDate.date(i) });
    }


    const handleClickDate = (formattedDate) => {
        const clickedDateObj = dayjs(formattedDate);
        const todayObj = dayjs().startOf("day");

        if (clickedDateObj.isAfter(todayObj)) {
            toast.error("일상점검표는 현재일과 과거일만 작성 할 수 있습니다.");
            return;
        }

        const hasData = dbHistoryList.some((item) => item.check_date === formattedDate);

        if (hasData) {
            navigate(`/inspection/history?date=${formattedDate}`);
        } else {
            setTargetDate(formattedDate);
            setShowConfirmModal(true);
        }
    };

    const handleModalConfirm = () => {
        setShowConfirmModal(false);
        navigate(`/inspection/daily_check?date=${targetDate}`);
    };


    const handleExportCSV = () => {
        if (dbHistoryList.length === 0) {
            toast.error("해당 월에 등록된 점검 데이터가 없습니다.");
            return;
        }

        setDataDownloading(true);

        try {
            const XLSX = require("xlsx-js-style");

            const headers = [
                "점검일자", "운전자명", "차량번호", "운송사업자명", "조치기록",
                "점검1-1", "점검1-2", "점검1-3", "점검1-4",
                "점검2-1", "점검2-2", "점검2-3",
                "점검3-1", "점검3-2", "점검3-3", "점검3-4"
            ];

            const flagToText = { G: "양호", B: "불량", N: "미운행" };

            const rows = dbHistoryList.map((item) => {
                const itemMap = {};
                (item.itemList || []).forEach(sub => {
                    itemMap[sub.item_cd] = flagToText[sub.check_flag];
                });

                return [
                    item.check_date || "-",
                    item.user_nm || "-",
                    item.veh_number || "-",
                    item.tb_nm || "-",
                    item.check_msg || "-",

                    itemMap[3102],
                    itemMap[3103],
                    itemMap[3104],
                    itemMap[3105],
                    itemMap[3107],
                    itemMap[3108],
                    itemMap[3109],
                    itemMap[3111],
                    itemMap[3112],
                    itemMap[3113],
                    itemMap[3114]
                ];
            });

            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

            for (let cellRef in worksheet) {
                if (cellRef[0] === '!') continue;

                if (worksheet[cellRef]) {
                    worksheet[cellRef].s = {
                        alignment: {
                            horizontal: "center",
                            vertical: "center"
                        }
                    };
                }
            }

            worksheet["!cols"] = [
                { wch: 15 }, { wch: 10 }, { wch: 16 }, { wch: 20 }, { wch: 30 },
                { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 9 },
                { wch: 9 }, { wch: 9 }, { wch: 9 },
                { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 9 }
            ];

            XLSX.utils.book_append_sheet(workbook, worksheet, "일상점검현황");


            const formattedDate = currentDate.startOf("month").format("YYMMDD");
            const fileName = `운수종사자_일상점검표_${formattedDate}.xlsx`;
            const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const blob = new Blob([excelBuffer], { type: contentType });

            const reader = new FileReader();

            if (typeof window.TOPTOP_APP === 'object' && typeof window.TOPTOP_APP.getBase64FromBlobData === 'function') {
                console.log('Android 인앱 환경 엑셀 다운로드');
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                    const base64data = reader.result;
                    window.TOPTOP_APP.getBase64FromBlobData(fileName, contentType, base64data);
                };
            } else if (isIOS()) {
                console.log('iOS 인앱 환경 엑셀 다운로드');
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                    const base64data = reader.result;
                    const message = { filename: fileName, type: contentType, data: base64data };
                    window.webkit.messageHandlers.getBase64FromBlobData.postMessage(message);
                };
            } else {
                console.log('PC 브라우저 환경 엑셀 다운로드');
                const previewUrl = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = previewUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(previewUrl);
            }

            toast.success("엑셀 파일 다운로드가 완료되었습니다.");

        } catch (err) {
            console.error("엑셀 다운로드 실패:", err);
            toast.error("파일 다운로드 중 오류가 발생했습니다.");
        } finally {
            setDataDownloading(false);
        }
    };

    if (isLoading) return <Loading />;

    return (
        <div
            className="mobile-intro-wrapper d-flex flex-column"
            style={{
                background: "#F8F9FA",
                maxWidth: "600px",
                margin: "0 auto",
                width: "100%",
                minHeight: "100vh",
                height: "auto",
                padding: "65px 20px 50px 20px",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch"
            }}
        >
            <div className="d-flex align-items-center mb-3">
                <div className="w-100 bg-white rounded-3 p-3 d-flex align-items-center shadow-sm">
                    <div style={{ background: "#EBF3FF", width: "36px", height: "36px", borderRadius: "20%", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px" }}>
                        <img alt="" width="25" height="25" src={process.env.PUBLIC_URL + "/img/icon_registration_off.png"} />
                    </div>
                    <h4 className="m-0 fs-5 fw-bold" style={{ color: "#212529" }}>월별 점검 현황</h4>
                </div>
            </div>


            <div className="d-flex align-items-center justify-content-between mb-4 bg-white p-2 rounded-3 shadow-sm border">
                <button onClick={handlePrevMonth} className="btn border-0 p-2"><span className="fw-bold text-secondary" style={{ fontSize: "18px" }}>&lt;</span></button>
                <div onClick={handleTitleClick} className="fw-bold text-dark fs-16 d-flex align-items-center gap-1" style={{ cursor: "pointer", userSelect: "none" }}>
                    <span>{year}년 {month + 1}월</span>
                    <span className="text-muted" style={{ fontSize: "11px" }}>▼</span>
                </div>
                <input ref={monthInputRef} type="month" value={currentDate.format("YYYY-MM")} onChange={handleMonthInputChange} style={{ position: "absolute", width: "1px", height: "1px", padding: "0", margin: "-1px", overflow: "hidden", clip: "rect(0,0,0,0)", border: "0" }} />
                <button onClick={handleNextMonth} className="btn border-0 p-2"><span className="fw-bold text-secondary" style={{ fontSize: "18px" }}>&gt;</span></button>
            </div>


            <div className="bg-white rounded-4 p-3 shadow-sm border w-100">
                <div className="d-grid mb-2" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: "8px" }}>
                    {weekdaysShort.map((day, i) => (
                        <div key={i} className={`text-center fw-bold fs-12 pb-2 ${i === 0 ? "text-danger" : i === 6 ? "text-primary" : "text-muted"}`}>{day}</div>
                    ))}
                </div>
                <div className="d-grid" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: "10px 8px" }}>
                    {daysArray.map(({ day, currentMonth, date }, index) => {
                        const formattedDate = date.format("YYYY-MM-DD");
                        const isToday = date.isSame(today, "day");
                        const columnIndex = index % 7;

                        const hasData = dbHistoryList.some((item) => item.check_date === formattedDate);

                        return (
                            <div
                                key={index}
                                onClick={() => handleClickDate(formattedDate)}
                                className="d-flex flex-column align-items-center justify-content-center position-relative"
                                style={{
                                    height: "48px",
                                    borderRadius: "12px",
                                    fontSize: "14px",
                                    fontWeight: isToday || hasData ? "bold" : "normal",
                                    cursor: "pointer",
                                    color: hasData
                                        ? "#FFFFFF"
                                        : !currentMonth
                                            ? "#D1D5DB"
                                            : columnIndex === 0
                                                ? "#DC3545"
                                                : columnIndex === 6
                                                    ? "#0D6EFD"
                                                    : "#212529",
                                    background: hasData ? "#EAA135" : "transparent",
                                    border: isToday ? "2px solid #2F73F6" : "none",
                                }}
                            >
                                {day}
                            </div>
                        );
                    })}
                </div>
            </div>


            <DailyCheckConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleModalConfirm}
            />


            <div className="d-flex gap-2 align-items-center mb-4 mt-4">
                <button
                    type="button"
                    onClick={handleExportCSV}
                    className="btn text-white fw-bold w-100"
                    style={{
                        background: "#3A74C2",
                        borderRadius: "6px",
                        height: "50px",
                        width: "100%"
                    }}
                >
                    {dataDownloading ? (
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            role="status"
                            aria-label="loading"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="10"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                opacity="0.25"
                            />
                            <path
                                d="M12 2a10 10 0 0 1 10 10"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                            >
                                <animateTransform
                                    attributeName="transform"
                                    type="rotate"
                                    from="0 12 12"
                                    to="360 12 12"
                                    dur="0.8s"
                                    repeatCount="indefinite"
                                />
                            </path>
                        </svg>
                    ) : (
                        `${year}년 ${month + 1}월 점검 현황 내보내기`
                    )}

                </button>
            </div>
        </div>
    );
}